import React, { useState, useEffect } from 'react'
import { User, UserFormData, UserRole } from '../../../types'
import { X, Eye, EyeOff, User as UserIcon } from 'lucide-react'
import LevelBadge from '../../../components/common/LevelBadge'

interface UserFormProps {
  user?: User
  onSubmit: (data: UserFormData) => void
  onCancel: () => void
  isLoading: boolean
}

const UserForm: React.FC<UserFormProps> = ({
  user,
  onSubmit,
  onCancel,
  isLoading
}) => {
  const [formData, setFormData] = useState<UserFormData>({
    name: '',
    email: '',
    role: 'EMPLOYEE',
    level: 0,
    password: '',
    hourlyRate: undefined,
    bankInfo: {
      bankName: '',
      branchName: '',
      branchCode: '',
      accountNumber: '',
      accountHolder: ''
    },
    isActive: true,
    forcePasswordChange: false
  })

  const [showPassword, setShowPassword] = useState(false)
  const [errors, setErrors] = useState<Record<string, string>>({})

  // 如果是編輯模式，填入現有資料
  useEffect(() => {
    if (user) {
      setFormData({
        name: user.name,
        email: user.email,
        role: user.role,
        level: user.level,
        password: '', // 編輯時不顯示密碼
        hourlyRate: user.hourlyRate || undefined,
        bankInfo: user.bankInfo ? {
          bankName: user.bankInfo.bankName,
          branchName: user.bankInfo.branchName,
          branchCode: user.bankInfo.branchCode || '',
          accountNumber: user.bankInfo.accountNumber,
          accountHolder: user.bankInfo.accountHolder
        } : {
          bankName: '',
          branchName: '',
          branchCode: '',
          accountNumber: '',
          accountHolder: ''
        },
        isActive: user.isActive,
        forcePasswordChange: false
      })
    }
  }, [user])

  const handleInputChange = (field: keyof UserFormData, value: any) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }))
    
    // 清除該欄位的錯誤
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: ''
      }))
    }
  }

  const handleBankInfoChange = (field: string, value: string) => {
    setFormData(prev => ({
      ...prev,
      bankInfo: {
        ...prev.bankInfo!,
        [field]: value
      }
    }))
  }

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {}

    // 必填欄位驗證
    if (!formData.name.trim()) {
      newErrors.name = '姓名為必填欄位'
    }

    if (!formData.email.trim()) {
      newErrors.email = '電子信箱為必填欄位'
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = '請輸入有效的電子信箱格式'
    }

    if (!formData.role) {
      newErrors.role = '角色為必填欄位'
    }

    // 新增用戶時密碼為必填
    if (!user && !formData.password) {
      newErrors.password = '密碼為必填欄位'
    }

    // 密碼強度驗證
    if (formData.password && formData.password.length < 6) {
      newErrors.password = '密碼長度至少需要 6 個字元'
    }

    // 時薪驗證
    if (formData.hourlyRate !== undefined && formData.hourlyRate !== null) {
      if (!Number.isInteger(formData.hourlyRate) || formData.hourlyRate < 0) {
        newErrors.hourlyRate = '時薪必須為正整數'
      }
    }

    // 銀行資訊驗證（如果有填寫任一欄位，則全部都要填寫）
    const bankInfo = formData.bankInfo!
    const hasBankInfo = bankInfo.bankName || bankInfo.branchName || bankInfo.accountNumber || bankInfo.accountHolder
    
    if (hasBankInfo) {
      if (!bankInfo.bankName) newErrors.bankName = '銀行名稱為必填欄位'
      if (!bankInfo.branchName) newErrors.branchName = '分行名稱為必填欄位'
      if (!bankInfo.accountNumber) newErrors.accountNumber = '帳戶號碼為必填欄位'
      if (!bankInfo.accountHolder) newErrors.accountHolder = '帳戶戶名為必填欄位'
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    
    if (!validateForm()) {
      return
    }

    // 準備提交資料
    const submitData: UserFormData = {
      ...formData,
      // 如果銀行資訊都是空的，就不要包含
      bankInfo: formData.bankInfo && Object.values(formData.bankInfo).some(v => v) 
        ? formData.bankInfo 
        : undefined
    }

    // 編輯模式下，如果密碼為空則不包含密碼欄位
    if (user && !formData.password) {
      delete submitData.password
    }

    onSubmit(submitData)
  }

  const roleOptions = [
    { value: 'EMPLOYEE', label: '員工' },
    { value: 'MANAGER', label: '經理' },
    { value: 'ADMIN', label: '管理員' }
  ]

  return (
    <div className="modal-overlay">
      <div className="modal-content max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        {/* 標題 */}
        <div className="flex items-center justify-between p-6 border-b border-primary-200">
          <div className="flex items-center space-x-3">
            <UserIcon className="w-6 h-6 text-primary-600" />
            <h2 className="text-xl font-semibold text-secondary-800">
              {user ? '編輯成員資訊' : '新增團隊成員'}
            </h2>
          </div>
          <button
            onClick={onCancel}
            className="text-secondary-400 hover:text-secondary-600 transition-colors"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* 表單內容 */}
        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* 基本資訊 */}
          <div>
            <h3 className="text-lg font-medium text-secondary-800 mb-4">基本資訊</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* 姓名 */}
              <div>
                <label className="block text-sm font-medium text-secondary-700 mb-2">
                  姓名 <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => handleInputChange('name', e.target.value)}
                  className={`input-primary ${errors.name ? 'input-error' : ''}`}
                  placeholder="輸入姓名..."
                />
                {errors.name && (
                  <p className="text-red-500 text-sm mt-1">{errors.name}</p>
                )}
              </div>

              {/* 電子信箱 */}
              <div>
                <label className="block text-sm font-medium text-secondary-700 mb-2">
                  電子信箱 <span className="text-red-500">*</span>
                </label>
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) => handleInputChange('email', e.target.value)}
                  className={`input-primary ${errors.email ? 'input-error' : ''}`}
                  placeholder="輸入電子信箱..."
                />
                {errors.email && (
                  <p className="text-red-500 text-sm mt-1">{errors.email}</p>
                )}
              </div>

              {/* 角色 */}
              <div>
                <label className="block text-sm font-medium text-secondary-700 mb-2">
                  角色 <span className="text-red-500">*</span>
                </label>
                <select
                  value={formData.role}
                  onChange={(e) => handleInputChange('role', e.target.value as UserRole)}
                  className={`input-primary ${errors.role ? 'input-error' : ''}`}
                >
                  {roleOptions.map(option => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
                {errors.role && (
                  <p className="text-red-500 text-sm mt-1">{errors.role}</p>
                )}
              </div>

              {/* 職等 */}
              <div>
                <label className="block text-sm font-medium text-secondary-700 mb-2">
                  職等
                </label>
                <div className="space-y-2">
                  <select
                    value={formData.level || 0}
                    onChange={(e) => handleInputChange('level', parseInt(e.target.value))}
                    className="input-primary"
                  >
                    {Array.from({ length: 10 }, (_, i) => (
                      <option key={i} value={i}>
                        Lv{i}
                      </option>
                    ))}
                  </select>
                  {formData.level !== undefined && (
                    <div className="flex items-center space-x-2">
                      <span className="text-sm text-secondary-600">預覽：</span>
                      <LevelBadge level={formData.level} size="sm" showLabel />
                    </div>
                  )}
                </div>
              </div>

              {/* 密碼 */}
              <div>
                <label className="block text-sm font-medium text-secondary-700 mb-2">
                  密碼 {!user && <span className="text-red-500">*</span>}
                  {user && <span className="text-sm text-secondary-500">(留空表示不變更)</span>}
                </label>
                <div className="relative">
                  <input
                    type={showPassword ? 'text' : 'password'}
                    value={formData.password}
                    onChange={(e) => handleInputChange('password', e.target.value)}
                    className={`input-primary pr-10 ${errors.password ? 'input-error' : ''}`}
                    placeholder={user ? "輸入新密碼..." : "輸入密碼..."}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-secondary-400 hover:text-secondary-600"
                  >
                    {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
                {errors.password && (
                  <p className="text-red-500 text-sm mt-1">{errors.password}</p>
                )}
              </div>
            </div>
          </div>

          {/* 薪資設定 */}
          <div>
            <h3 className="text-lg font-medium text-secondary-800 mb-4">薪資設定</h3>
            <div>
              <label className="block text-sm font-medium text-secondary-700 mb-2">
                基本時薪 (整數)
              </label>
              <div className="flex items-center space-x-2">
                <span className="text-secondary-600">NT$</span>
                <input
                  type="number"
                  min="0"
                  step="1"
                  value={formData.hourlyRate || ''}
                  onChange={(e) => handleInputChange('hourlyRate', e.target.value ? parseInt(e.target.value) : undefined)}
                  className={`input-primary flex-1 ${errors.hourlyRate ? 'input-error' : ''}`}
                  placeholder="500"
                />
                <span className="text-secondary-600">/小時</span>
              </div>
              {errors.hourlyRate && (
                <p className="text-red-500 text-sm mt-1">{errors.hourlyRate}</p>
              )}
            </div>
          </div>

          {/* 銀行匯款資訊 */}
          <div>
            <h3 className="text-lg font-medium text-secondary-800 mb-4">銀行匯款資訊</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* 銀行名稱 */}
              <div>
                <label className="block text-sm font-medium text-secondary-700 mb-2">
                  銀行名稱
                </label>
                <input
                  type="text"
                  value={formData.bankInfo?.bankName || ''}
                  onChange={(e) => handleBankInfoChange('bankName', e.target.value)}
                  className={`input-primary ${errors.bankName ? 'input-error' : ''}`}
                  placeholder="輸入銀行名稱..."
                />
                {errors.bankName && (
                  <p className="text-red-500 text-sm mt-1">{errors.bankName}</p>
                )}
              </div>

              {/* 分行名稱 */}
              <div>
                <label className="block text-sm font-medium text-secondary-700 mb-2">
                  分行名稱
                </label>
                <input
                  type="text"
                  value={formData.bankInfo?.branchName || ''}
                  onChange={(e) => handleBankInfoChange('branchName', e.target.value)}
                  className={`input-primary ${errors.branchName ? 'input-error' : ''}`}
                  placeholder="輸入分行名稱..."
                />
                {errors.branchName && (
                  <p className="text-red-500 text-sm mt-1">{errors.branchName}</p>
                )}
              </div>

              {/* 帳戶號碼 */}
              <div>
                <label className="block text-sm font-medium text-secondary-700 mb-2">
                  帳戶號碼
                </label>
                <input
                  type="text"
                  value={formData.bankInfo?.accountNumber || ''}
                  onChange={(e) => handleBankInfoChange('accountNumber', e.target.value)}
                  className={`input-primary ${errors.accountNumber ? 'input-error' : ''}`}
                  placeholder="輸入帳戶號碼..."
                />
                {errors.accountNumber && (
                  <p className="text-red-500 text-sm mt-1">{errors.accountNumber}</p>
                )}
              </div>

              {/* 帳戶戶名 */}
              <div>
                <label className="block text-sm font-medium text-secondary-700 mb-2">
                  帳戶戶名
                </label>
                <input
                  type="text"
                  value={formData.bankInfo?.accountHolder || ''}
                  onChange={(e) => handleBankInfoChange('accountHolder', e.target.value)}
                  className={`input-primary ${errors.accountHolder ? 'input-error' : ''}`}
                  placeholder="輸入帳戶戶名..."
                />
                {errors.accountHolder && (
                  <p className="text-red-500 text-sm mt-1">{errors.accountHolder}</p>
                )}
              </div>
            </div>
          </div>

          {/* 帳號設定 */}
          <div>
            <h3 className="text-lg font-medium text-secondary-800 mb-4">帳號設定</h3>
            <div className="space-y-3">
              <label className="flex items-center space-x-3">
                <input
                  type="checkbox"
                  checked={formData.isActive}
                  onChange={(e) => handleInputChange('isActive', e.target.checked)}
                  className="w-4 h-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
                />
                <span className="text-sm text-secondary-700">啟用帳號</span>
              </label>

              {!user && (
                <label className="flex items-center space-x-3">
                  <input
                    type="checkbox"
                    checked={formData.forcePasswordChange}
                    onChange={(e) => handleInputChange('forcePasswordChange', e.target.checked)}
                    className="w-4 h-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
                  />
                  <span className="text-sm text-secondary-700">強制下次登入時更改密碼</span>
                </label>
              )}
            </div>
          </div>

          {/* 操作按鈕 */}
          <div className="flex justify-end space-x-4 pt-6 border-t border-primary-200">
            <button
              type="button"
              onClick={onCancel}
              className="btn-secondary"
              disabled={isLoading}
            >
              取消
            </button>
            <button
              type="submit"
              className="btn-primary"
              disabled={isLoading}
            >
              {isLoading ? (
                <div className="flex items-center space-x-2">
                  <div className="loading-spinner w-4 h-4"></div>
                  <span>處理中...</span>
                </div>
              ) : (
                user ? '更新成員' : '建立成員'
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

export default UserForm
